<template>
        <Dialog class="reader-account-dialog" :visible="visible && !isAvatarPickerOpen && !isDeleteOpen && !!sessionUser" modal append-to="self" aria-labelledby="reader-account-profile-title" :draggable="false" @update:visible="close">
                <template #header>
                        <span id="reader-account-profile-title" class="p-dialog-title" tabindex="-1" autofocus>Профиль</span>
                </template>
                <template v-if="sessionUser">
                        <div class="profile-avatar-row">
                                <span class="profile-avatar-frame">
                                        <span class="account-avatar" :class="`account-avatar--${sessionUser.avatarId}`" role="img" aria-label="Аватар аккаунта"></span>
                                </span>
                                <div class="profile-avatar-settings">
                                        <div class="profile-avatar-label">Аватар</div>
                                        <p>Выберите аватар из предложенных</p>
                                        <Button type="button" label="Изменить" variant="outlined" @click="openAvatarPicker(sessionUser.avatarId)" />
                                </div>
                        </div>
                        <div class="profile-email"><div class="profile-email-label">Email</div><p>{{ sessionUser.email }}</p></div>
                        <a href="#" class="delete-account" @click.prevent="isDeleteOpen = true">Удалить аккаунт</a>
                </template>
        </Dialog>

        <Dialog class="reader-account-dialog" :visible="visible && isAvatarPickerOpen && !!sessionUser" modal append-to="self" aria-labelledby="reader-account-avatar-title" :draggable="false" :close-on-escape="!isSavingAvatar" :close-button-props="{ severity: 'secondary', text: true, rounded: true, disabled: isSavingAvatar }" @update:visible="cancelAvatarPicker">
                <template #header>
                        <span id="reader-account-avatar-title" class="p-dialog-title" tabindex="-1" autofocus>Выберите аватар</span>
                </template>
                <div class="avatar-options" role="group" aria-label="Аватары">
                        <button v-for="avatar in accountAvatars" :key="avatar.id" class="avatar-option" type="button" :aria-label="avatar.label" :aria-pressed="selectedAvatarId === avatar.id" :disabled="isSavingAvatar" @click="selectedAvatarId !== avatar.id && (selectedAvatarId = avatar.id)">
                                <span class="account-avatar" :class="`account-avatar--${avatar.id}`" aria-hidden="true"></span>
                        </button>
                </div>
                <p v-if="message" class="profile-message" role="status">{{ message }}</p>
                <div class="profile-dialog-actions">
                        <Button type="button" class="avatar-picker-cancel" label="Отменить" variant="outlined" :disabled="isSavingAvatar" @click="cancelAvatarPicker" />
                        <Button type="button" class="avatar-picker-save" :label="isSavingAvatar ? 'Сохранение…' : 'Сохранить'" :disabled="!selectedAvatarId || isSavingAvatar" :aria-busy="isSavingAvatar" @click="saveAvatar" />
                </div>
        </Dialog>

        <Dialog class="reader-account-dialog account-delete-dialog" :visible="visible && isDeleteOpen && !!sessionUser" modal append-to="self" header="Удалить аккаунт" :draggable="false" :closable="false" :close-on-escape="!isDeleting" @update:visible="cancelDelete">
                <div class="account-delete-warning"><i class="pi pi-exclamation-circle" aria-hidden="true"></i><p>Все данные в этом аккаунте будут удалены без возможности восстановления.</p></div>
                <p>Вы уверены, что хотите удалить аккаунт?</p>
                <p v-if="message" class="profile-message" role="status">{{ message }}</p>
                <div class="profile-dialog-actions">
                        <Button type="button" label="Отменить" variant="outlined" :disabled="isDeleting" @click="cancelDelete" />
                        <Button type="button" :label="isDeleting ? 'Удаление…' : 'Удалить'" severity="danger" :disabled="isDeleting" @click="deleteAccount" />
                </div>
        </Dialog>
</template>

<script setup lang="ts">
        import { ref, watch } from "vue"
        import { deleteSessionAccount, saveAccountAvatar, sessionUser } from "@/ts/api/auth-session"
        import "@/less/account-avatars.less"
        import "@/less/account-profile.less"

        const accountAvatars = [
                { id: "girl-reader", label: "Девочка с книгой" },
                { id: "boy-reader", label: "Мальчик с книгой" },
                { id: "girl-glasses", label: "Девочка в очках" },
                { id: "cat-reader", label: "Котёнок с книгой" },
                { id: "boy-headphones", label: "Мальчик в наушниках" },
                { id: "girl-pink", label: "Девочка в розовом" },
                { id: "boy-dark-hair", label: "Темноволосый мальчик" },
                { id: "robot-reader", label: "Робот с книгой" },
        ]

        const props = defineProps<{ visible: boolean }>()
        const emit = defineEmits<{ 'update:visible': [value: boolean] }>()
        const isAvatarPickerOpen = ref(false)
        const isDeleteOpen = ref(false)
        const isDeleting = ref(false)
        const isSavingAvatar = ref(false)
        const selectedAvatarId = ref('')
        const message = ref('')

        watch(() => props.visible, visible => {
                if (visible) {
                        isAvatarPickerOpen.value = false
                        isDeleteOpen.value = false
                        message.value = ''
                }
        })
        watch(sessionUser, user => { if (!user) close() })

        function close() { emit('update:visible', false) }
        function openAvatarPicker(avatarId: string) {
                selectedAvatarId.value = avatarId
                message.value = ''
                isAvatarPickerOpen.value = true
        }
        function cancelAvatarPicker() {
                if (isSavingAvatar.value) return
                isAvatarPickerOpen.value = false
                message.value = ''
        }
        async function saveAvatar() {
                if (isSavingAvatar.value) return
                isSavingAvatar.value = true
                message.value = ''
                try {
                        await saveAccountAvatar(selectedAvatarId.value)
                        isAvatarPickerOpen.value = false
                } catch (error) {
                        message.value = error instanceof Error ? error.message : 'Не удалось сохранить аватар.'
                } finally {
                        isSavingAvatar.value = false
                }
        }
        function cancelDelete() {
                if (isDeleting.value) return
                isDeleteOpen.value = false
                message.value = ''
        }
        async function deleteAccount() {
                if (isDeleting.value) return
                isDeleting.value = true
                message.value = ''
                try {
                        await deleteSessionAccount()
                        close()
                } catch (error) {
                        message.value = error instanceof Error ? error.message : 'Не удалось удалить аккаунт.'
                } finally {
                        isDeleting.value = false
                }
        }
</script>
